import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { fetchUserProfile, MessageLocalExpertButton } from './ProfileLink';
import { getPublicName } from './UserProfile';
import UserAvatar from './UserAvatar';

export function getSeasonalNote(date = new Date()) {
  const month = date.getMonth();
  if (month >= 5 && month <= 8) {
    return 'Summer trips are being planned — worth checking what is listed near your destination.';
  }
  if (month >= 11 || month <= 1) {
    return 'Cold-weather gear moves fast this time of year if you are heading somewhere snowy.';
  }
  if (month >= 2 && month <= 4) {
    return 'Spring is a good time to line up gear before the busy season hits.';
  }
  return 'Planning ahead? Search by location or post what you need for an upcoming trip.';
}

export function CommunityPulse({
  inventoryCount = 0,
  wantedCount = 0,
  completedRentals = 0,
  expertCount = 0,
}) {
  const seasonalNote = getSeasonalNote();
  const parts = [];

  if (inventoryCount > 0) {
    parts.push(`${inventoryCount} ${inventoryCount === 1 ? 'item' : 'items'} listed`);
  }
  if (wantedCount > 0) {
    parts.push(`${wantedCount} open ${wantedCount === 1 ? 'request' : 'requests'} from members`);
  }
  if (completedRentals > 0) {
    parts.push(`${completedRentals} completed ${completedRentals === 1 ? 'rental' : 'rentals'}`);
  }
  if (expertCount > 0) {
    parts.push(`${expertCount} local ${expertCount === 1 ? 'expert' : 'experts'} willing to answer questions`);
  }

  return (
    <section className="community-pulse" aria-label="Community activity">
      {parts.length > 0 ? (
        <p className="community-pulse-stats">
          Right now on the platform: {parts.join(' · ')}.
        </p>
      ) : (
        <p className="community-pulse-stats">
          We are early and growing — a community of people who would rather share gear than let it collect dust.
          List something you own, or post what you need for a trip.
        </p>
      )}
      <p className="community-pulse-seasonal">{seasonalNote}</p>
    </section>
  );
}

function LocalExpertCard({ expert, currentUser }) {
  const name = getPublicName(expert, 'Member');
  const location = expert.location?.trim() || '';
  const aboutSnippet = expert.aboutMe?.trim()
    ? expert.aboutMe.trim().slice(0, 140) + (expert.aboutMe.trim().length > 140 ? '…' : '')
    : location
      ? `${name} knows the ${location} area and is open to questions.`
      : `${name} is open to sharing local knowledge.`;

  return (
    <article className="local-expert-card">
      <div className="local-expert-card-header">
        <UserAvatar profile={expert} userId={expert.id} name={name} size={44} />
        <div>
          <h3>
            <Link to={`/profile/${expert.id}`}>{name}</Link>
          </h3>
          {location && <p className="local-expert-card-location">{location}</p>}
        </div>
      </div>
      <p className="local-expert-card-about">{aboutSnippet}</p>
      <MessageLocalExpertButton
        user={currentUser}
        expertId={expert.id}
        expertLocation={location}
        label="Ask about the area"
        subtle
      />
    </article>
  );
}

export function LocalExpertStrip({ user, expertIds = [] }) {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadExperts = async () => {
      setLoading(true);
      try {
        let ids = expertIds;
        if (!ids.length) {
          const snapshot = await getDocs(
            query(collection(db, 'users'), where('localExpert', '==', true), limit(12))
          );
          ids = snapshot.docs.map((docSnap) => docSnap.id);
        }

        const profiles = await Promise.all(ids.map((id) => fetchUserProfile(id)));
        const withLocation = profiles
          .filter((profile) => profile && profile.localExpert)
          .sort((a, b) => {
            const aHas = Boolean(a.location?.trim());
            const bHas = Boolean(b.location?.trim());
            if (aHas !== bHas) return bHas - aHas;
            return getPublicName(a).localeCompare(getPublicName(b));
          })
          .slice(0, 4);

        if (mounted) setExperts(withLocation);
      } catch (error) {
        console.error('Error loading local experts:', error);
        if (mounted) setExperts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadExperts();
    return () => {
      mounted = false;
    };
  }, [expertIds]);

  if (loading) {
    return (
      <section id="local-experts" className="local-expert-strip" aria-label="Local experts">
        <h2 className="home-section-title">People who know the area</h2>
        <p className="home-section-lead">Loading local experts…</p>
      </section>
    );
  }

  return (
    <section id="local-experts" className="local-expert-strip" aria-label="Local experts">
      <h2 className="home-section-title">People who know the area</h2>
      <p className="home-section-lead">
        Some members have offered to answer questions about trails, water, campsites, and local spots —
        even if you are not renting their gear.
      </p>
      {experts.length > 0 ? (
        <div className="local-expert-grid">
          {experts.map((expert) => (
            <LocalExpertCard key={expert.id} expert={expert} currentUser={user} />
          ))}
        </div>
      ) : (
        <p className="home-section-lead">
          Local experts will appear here as members opt in on their profile. Until then,{' '}
          <Link to="/#gear-wanted">post what you need for your trip</Link> and the community can respond.
        </p>
      )}
    </section>
  );
}

export async function fetchCommunityStats() {
  try {
    const [rentalsSnap, wantedSnap, expertsSnap] = await Promise.all([
      getDocs(query(collection(db, 'rentals'), where('status', '==', 'completed'), limit(100))),
      getDocs(query(collection(db, 'gearWanted'), where('status', '==', 'open'), limit(100))),
      getDocs(query(collection(db, 'users'), where('localExpert', '==', true), limit(50))),
    ]);

    return {
      completedRentals: rentalsSnap.size,
      wantedCount: wantedSnap.size,
      expertCount: expertsSnap.size,
    };
  } catch (error) {
    console.error('Error loading community stats:', error);
    return {
      completedRentals: 0,
      wantedCount: 0,
      expertCount: 0,
    };
  }
}

export function SearchEmptyWantedPrompt() {
  return (
    <div className="search-empty-wanted">
      <p>
        Planning a trip and nothing matches yet? Tell the community what you need — someone nearby may have it for your
        dates.
      </p>
      <Link to="/#gear-wanted" className="search-empty-wanted-link">
        Post what you need for your trip
      </Link>
    </div>
  );
}